import 'whatwg-fetch';
import isEmpty from 'lodash/isEmpty';
import CapNotification from '@capillarytech/cap-ui-library/CapNotification';
import { removeAuthenticationDetais } from '../utils/authWrapper';
import config from '../config/app';
import * as requestConstructor from './requestConstructor';
import { BADGES_ENDPOINT } from '../utils/constants';
import { MODE_UPDATE } from '../components/pages/App/constants';

const { getAPICallObject } = requestConstructor;

let API_ENDPOINT = config.development.api_endpoint;
let API_AUTH_ENDPOINT = config.development.auth_endpoint;
const isProd = process.env.NODE_ENV === 'production';

if (isProd) {
  API_ENDPOINT = config.production.api_endpoint;
  API_AUTH_ENDPOINT = config.production.auth_endpoint;
}

function parseJSON(response) {
  return response.json();
}

function redirectIfUnauthenticated(response) {
  const isUnauthorized = response.status === 401;
  const isLoginPage = window.location.pathname.indexOf('/login') !== -1;
  const isAuthUserCall =
    response.url.split('auth')[1] &&
    response.url.split('auth')[1].split('?')[0] === '/user';
  if (isUnauthorized) {
    if (isProd) {
      const originUrl = window.location.origin;
      removeAuthenticationDetais();
      window.location = `${originUrl}${config.production.logout_url}`;
    } else {
      if (isLoginPage && isAuthUserCall) return;
      removeAuthenticationDetais();
      const loginPage = `${config.development.login_url}`;
      window.location = `${loginPage}`;
    }
  }
}

function checkStatus(response) {
  if (
    (response.status >= 200 && response.status < 300) ||
    response.status === 500 ||
    response.status === 400 ||
    response.status === 403 ||
    response.status === 409
  ) {
    return response;
  }

  redirectIfUnauthenticated(response);

  const error = new Error(response.statusText);
  error.response = response;
  throw error;
}

function fetchWithTimeout(ms, promise) {
  //https://github.com/github/fetch/issues/175#issuecomment-125779262
  return new Promise(function(resolve, reject) {
    setTimeout(function() {
      reject({
        message: 'Request timeout',
        errorLocation: window.location.href,
      });
    }, ms);
    promise.then(resolve, reject);
  });
}
function showError(error, status) {
  const capNotificationSeverityType =
    status / 100 === 4 ? CapNotification.info : CapNotification.error;

  let message = error.message;
  if (typeof error.message === 'object') {
    message = error.message.errorMessage;
  } else if (typeof error.result === 'object') {
    message = error.result.message;
    if (error?.result?.errorMessage?.errors?.[0]?.message)
      message = error.result.errorMessage.errors[0].message;
  }
  message = message || 'An error occurred with the API';

  capNotificationSeverityType({
    message,
  });
}
function checkStatusCode(res) {
  if (res && res.code) {
    return /^[4-5][0-9][0-9]$/.test(res.code);
  }
  return false;
}
function request(url, options, timeout = 50000) {
  const fetchUrl =
    url.indexOf('?') !== -1
      ? `${url}&time=${Date.now()}`
      : `${url}?time=${Date.now()}`;
  try {
    return fetchWithTimeout(timeout, fetch(fetchUrl, options))
      .then(checkStatus)
      .then(parseJSON)
      .then(res => {
        if (
          !isEmpty(res.errors) ||
          checkStatusCode(res) ||
          (res.success !== undefined && !res.success)
        ) {
          showError(res, res.status);
        }
        return res;
      })
      .catch(error => {
        if (error) {
          showError(error);
        }
        return error;
      });
  } catch (err) {
    if (err) {
      showError(err);
    }
    return err;
    // eslint-disable-next-line
  }
}

export const getLocizeMessage = async locale => {
  /** Commented api call as multi language support is not required as of now */

  const appNameList = ['badges_ui'];
  const response = await Promise.all(
    appNameList.map(appName => {
      const url = `${API_AUTH_ENDPOINT}/translations/${appName}/${locale}`;
      return request(url, getAPICallObject('GET'));
    }),
  );
  let data = {};
  response.forEach(item => {
    data = {
      ...data,
      ...item,
    };
  });
  return data;
};

export const logout = () => {
  const url = `${API_AUTH_ENDPOINT}/auth/logout`;
  return request(url, getAPICallObject('GET'));
};

export const changeProxyOrg = orgId => {
  const url = `${API_AUTH_ENDPOINT}/auth/setProxy/${orgId}`;
  return request(url, getAPICallObject('Post'));
};

export const getUserData = () => {
  const url = `${API_AUTH_ENDPOINT}/auth/user`;
  return request(url, getAPICallObject('GET'));
};

export const getAllUsers = orgId => {
  const url = `${API_AUTH_ENDPOINT}/auth/org/${orgId}/users`;
  return request(url, getAPICallObject('GET'));
};

export const getBadge = (badgeId, isBenefitEnabled = false) => {
  const url = `${API_ENDPOINT}${BADGES_ENDPOINT}${badgeId}?isBenefitEnabled=${isBenefitEnabled}`;
  return request(url, getAPICallObject('GET'));
};

export const getBadgesList = ({ param }) => {
  let url = `${API_ENDPOINT}/badges`;
  Object.keys(param).map((item, index) => {
    url +=
      param[item] != null
        ? `${index === 0 ? '?' : '&'}${item}=${param[item]}`
        : '';
  });
  return request(url, getAPICallObject('GET'));
};

export const updateBadgeStatus = (badgeId, status) => {
  const url = `${API_ENDPOINT}${BADGES_ENDPOINT}${badgeId}/${status}`;
  return request(url, getAPICallObject('PUT'));
};

export const getOwnerDetails = badgeId => {
  const url = `${API_ENDPOINT}${BADGES_ENDPOINT}${badgeId}/ownerDetails`;
  return request(url, getAPICallObject('GET'));
};

export const putBadgeImages = (badgeId, payload) => {
  const data = new FormData();
  if (payload.unearnedImage) {
    data.append('unearnedFile', payload.unearnedImage);
  }
  if (payload.earnedImage) {
    data.append('earnedFile', payload.earnedImage);
  }
  if (payload.inProgressImage) {
    data.append('inProgressFile', payload.inProgressImage);
  }
  data.append('unearnedParams', JSON.stringify(payload.unearnedParams));
  data.append('earnedParams', JSON.stringify(payload.earnedParams));
  data.append('inProgressParams', JSON.stringify(payload.inProgressParams));
  const url = `${API_ENDPOINT}${BADGES_ENDPOINT}${badgeId}/images`;
  return request(url, getAPICallObject('PUT', data, true));
};

export const getCustomFields = ({param}) => {
  let url = `${API_ENDPOINT}${BADGES_ENDPOINT}custom-fields/settings`;
  Object.keys(param).map((item, index) => {
    url +=
      param[item] != null
        ? `${index === 0 ? '?' : '&'}${item}=${param[item]}`
        : '';
  });
  return request(url, getAPICallObject('GET'));
};
export const saveCustomField = (payload, isPost) => {
  const method = isPost ? 'POST' : 'PUT';
  let url = method === 'PUT' 
      ? `${API_ENDPOINT}${BADGES_ENDPOINT}custom-fields/settings/${payload.id}` :
      `${API_ENDPOINT}${BADGES_ENDPOINT}custom-fields/settings`;
  return request(url, getAPICallObject(method, payload));
};

export const checkBadgeExists = ({ param }) => {
  let url = `${API_ENDPOINT}/badges/checkBadgeExists`;
  let isFirstParam = true;

  Object.keys(param).forEach(item => {
    if (Array.isArray(param[item])) {
      param[item].forEach(value => {
        if (value != null) {
          url += `${isFirstParam ? '?' : '&'}${item}=${value}`;
          isFirstParam = false;
        }
      });
    } else if (param[item] != null) {
      url += `${isFirstParam ? '?' : '&'}${item}=${param[item]}`;
      isFirstParam = false;
    }
  });

  return request(url, getAPICallObject('GET'));
};

export const getBadgeGroups = ({ param }) => {
  let url = `${API_ENDPOINT}/badges/badge-groups`;
  Object.keys(param).map((item, index) => {
    url +=
      param[item] != null
        ? `${index === 0 ? '?' : '&'}${item}=${param[item]}`
        : '';
  });
  return request(url, getAPICallObject('GET'));
};

export const createBadge = payload => {
  const data = new FormData();
  if (payload.unearnedImage) {
    data.append('unearnedFile', payload.unearnedImage);
  }
  if (payload.earnedImage) {
    data.append('earnedFile', payload.earnedImage);
  }
  if (payload.inProgressImage) {
    data.append('inProgressFile', payload.inProgressImage);
  }
  if (payload.unearnedParams) {
    data.append('unearnedParams', JSON.stringify(payload.unearnedParams));
  }
  if (payload.inProgressParams) {
    data.append('inProgressParams', JSON.stringify(payload.inProgressParams));
  }
  if (payload.earnedParams) {
    data.append('earnedParams', JSON.stringify(payload.earnedParams));
  }
  if (payload.isDuplication) {
    data.append('isDuplication', JSON.stringify(payload.isDuplication));
  }

  data.append('createBadgeParams', JSON.stringify(payload.createBadgeParams));
  let url = `${API_ENDPOINT}/badges`;
  return request(url, getAPICallObject('POST', data, true));
};

export const updateBadge = payload => {
  const data = new FormData();
  if (payload.unearnedImage) {
    data.append('unearnedFile', payload.unearnedImage);
  }
  if (payload.earnedImage) {
    data.append('earnedFile', payload.earnedImage);
  }
  if (payload.inProgressImage) {
    data.append('inProgressFile', payload.inProgressImage);
  }
  if (payload.unearnedParams) {
    const update = {
      ...payload.unearnedParams,
      mode: payload.unearnedParams.mode ?? MODE_UPDATE,
    };
    data.append('unearnedParams', JSON.stringify(update));
  }
  if (payload.inProgressParams) {
    const update = {
      ...payload.inProgressParams,
      mode: payload.inProgressParams.mode ?? MODE_UPDATE,
    };
    data.append('inProgressParams', JSON.stringify(update));
  }
  if (payload.earnedParams) {
    const update = {
      ...payload.earnedParams,
      mode: payload.earnedParams.mode ?? MODE_UPDATE,
    };
    data.append('earnedParams', JSON.stringify(update));
  }
  data.append('createBadgeParams', JSON.stringify(payload.createBadgeParams));
  let url = `${API_ENDPOINT}/badges`;
  return request(url, getAPICallObject('PUT', data, true));
};

export const getAllCustomFields = ({ param }) => {
  let url = `${API_ENDPOINT}${BADGES_ENDPOINT}custom-fields`;
  Object.keys(param).map((item, index) => {
    url +=
      param[item] != null
        ? `${index === 0 ? '?' : '&'}${item}=${param[item]}`
        : '';
  });
  return request(url, getAPICallObject('GET'));
};

export const getSupportedLocales = () => {
  const url = `${API_AUTH_ENDPOINT}/translations/supportedLocales`;
  return request(url, getAPICallObject('GET'));
};

export const saveBadgeGroup = ({ body }) => {
  const url = `${API_ENDPOINT}${BADGES_ENDPOINT}badge-groups`;

  return request(url, getAPICallObject('POST', body));
};

export const updateBadgeGroup = ({ body, groupId }) => {
  const url = `${API_ENDPOINT}${BADGES_ENDPOINT}badge-groups/${groupId}`;

  return request(url, getAPICallObject('PUT', body));
};

export const getPointBenefitDetails = ({ programId, allocationStrategyId, expirationStrategyId }) => {
  const url = `${API_ENDPOINT}${BADGES_ENDPOINT}point-benefit-details/${programId}/${allocationStrategyId}/${expirationStrategyId}`;
  return request(url, getAPICallObject('GET'));
};